import { ConfigurationValues } from '@cutr/constants/windows/windows.ts';
import {
  CONFIGURATIONS_OPTIONS,
  PaintColorValue as PaintColorValueType,
} from '@cutr/constants/windows/windows-configurations.ts';
import React from 'react';
import {
  Button,
  ComboBox,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import { ChevronDown } from '@/primitives/Icons';
import { Label } from '@/windows/primitives/Label';
import { Select, SelectItem } from '@/windows/primitives/Select';

import { unboxLabel } from '../Configurations';
import {
  ConfigurationsAction,
  useConfigurations,
  useResolvedSystem,
} from '../ConfigurationsContext';
import { PaintColorSwatch, PaintColorValue } from '../readonly/PaintColorValue';
import styles from './ColorConfigurationSelect.module.css';
import {
  filterConfigurationOptions,
  OptionsFilterFn,
} from './ConfigurationSelect';

type ColorConfigurationKey =
  | 'paintColorInside'
  | 'paintColorOutside'
  | 'aluCladdingColor'
  | 'rainGuardColor'
  | 'sashCoverColor';

type RalColorOption = Extract<PaintColorValueType, { colorType: 'RAL' }>;

const RAL_ITEM = { value: '__ral__' } as const;
const LEGACY_RAL_VALUE = 'ral';

export const matchesRalOption = ({
  ralCode,
  label,
  query,
}: {
  ralCode: string;
  label: string;
  query: string;
}) => {
  const normalized = query.trim().replace(/^ral\s*/i, '');
  if (/^\d*$/.test(normalized)) {
    return ralCode.startsWith(normalized);
  }
  return label.toLowerCase().includes(normalized.toLowerCase());
};

export const stripRal = (label: string) => label.replace(/RAL\s*/g, '').trim();

const ralLabel = (option: RalColorOption) => stripRal(unboxLabel(option.label));

const isRalOption = (option: PaintColorValueType): option is RalColorOption =>
  option.colorType === 'RAL';

const PaletteIcon = () => (
  <svg
    className={styles.paletteIcon}
    viewBox="3.5 2.5 25.5 26.5"
    fill="none"
    aria-hidden="true"
  >
    <g fill="white" stroke="#262626" strokeWidth="1.25">
      <rect
        x="24.7094"
        y="14.3117"
        width="7.01969"
        height="15.65"
        rx="0.625"
        transform="rotate(63.7469 24.7094 14.3117)"
      />
      <rect
        x="15.0279"
        y="7.50413"
        width="8.25"
        height="18.1925"
        rx="0.625"
        transform="rotate(26.914 15.0279 7.50413)"
      />
      <rect x="4.375" y="3.375" width="8.25" height="23.25" rx="0.625" />
    </g>
  </svg>
);

export const ColorConfigurationSelect = ({
  configurationKey,
  dispatchAction,
  optionsFilterFn,
}: {
  configurationKey: ColorConfigurationKey;
  dispatchAction: Extract<
    ConfigurationsAction,
    { value: PaintColorValueType }
  >['type'];
  optionsFilterFn?: OptionsFilterFn;
}) => {
  const { t } = useTranslation();
  const { configurations, dispatch } = useConfigurations();
  const system = useResolvedSystem();
  const labelId = React.useId();
  const [isRalPending, setIsRalPending] = React.useState(false);

  const selected: ConfigurationValues[ColorConfigurationKey] | undefined =
    configurations[configurationKey];
  const selectedRal = selected && isRalOption(selected) ? selected : undefined;
  const isRalMode = isRalPending || !!selectedRal;

  const options = filterConfigurationOptions({
    options: CONFIGURATIONS_OPTIONS[configurationKey].options,
    selectedValue: selected?.value,
    configurations: { ...configurations, system },
    optionsFilterFn,
  });
  const ralOptions = options.filter(isRalOption);
  const standardOptions = options.filter(
    (option) =>
      !isRalOption(option) &&
      (option.value !== LEGACY_RAL_VALUE || option.value === selected?.value)
  );
  const ralCodeByText = new Map(
    ralOptions.map((option) => [ralLabel(option), option.ralCode])
  );

  const select = (option: PaintColorValueType) => {
    setIsRalPending(false);
    dispatch({ type: dispatchAction, value: option });
  };

  return (
    <>
      <Label id={labelId}>
        {t(`windows.configurations.${configurationKey}.label`)}
      </Label>
      <div className={styles.fields} data-ral={isRalMode || undefined}>
        <Select
          aria-labelledby={labelId}
          items={[
            ...standardOptions.slice(0, 1),
            RAL_ITEM,
            ...standardOptions.slice(1),
          ]}
          selectedKey={isRalMode ? RAL_ITEM.value : (selected?.value ?? null)}
          onSelectionChange={(key) => {
            if (key === RAL_ITEM.value) {
              setIsRalPending(true);
              return;
            }
            const option = standardOptions.find((o) => o.value === key);
            if (option) {
              select(option);
            }
          }}
        >
          {(option) =>
            !('colorType' in option) ? (
              <SelectItem id={RAL_ITEM.value} textValue="RAL">
                <span className={styles.ralItem}>
                  <PaletteIcon />
                  {t('windows.configurations.colorSelect.ral')}
                </span>
              </SelectItem>
            ) : (
              <SelectItem id={option.value} textValue={option.value}>
                <PaintColorValue value={option} />
              </SelectItem>
            )
          }
        </Select>
        {isRalMode && (
          <ComboBox
            aria-labelledby={labelId}
            className={styles.comboBox}
            defaultItems={ralOptions}
            defaultFilter={(textValue, inputValue) =>
              matchesRalOption({
                ralCode: ralCodeByText.get(textValue) ?? '',
                label: textValue,
                query: inputValue,
              })
            }
            selectedKey={selectedRal?.value ?? null}
            onSelectionChange={(key) => {
              const option = ralOptions.find((o) => o.value === key);
              if (option) {
                select(option);
              }
            }}
            isRequired
            isInvalid={!selectedRal}
          >
            <div className={styles.comboBoxInput}>
              {selectedRal && <PaintColorSwatch value={selectedRal} />}
              <Input
                placeholder={t(
                  'windows.configurations.colorSelect.ralSearchPlaceholder'
                )}
              />
              <Button>
                <ChevronDown />
              </Button>
            </div>
            <Popover className={styles.popover}>
              <ListBox>
                {(option: RalColorOption) => (
                  <ListBoxItem
                    id={option.value}
                    textValue={ralLabel(option)}
                    className={styles.item}
                  >
                    <PaintColorValue value={option} label={ralLabel(option)} />
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </ComboBox>
        )}
      </div>
    </>
  );
};
